import React, { useState } from 'react';
import Select from 'components/ui/Select';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { supabase } from '../../../lib/supabase';

const DOCUMENT_TYPES = [
  { value: 'carta_identita', label: 'Carta d\'Identità' },
  { value: 'passaporto', label: 'Passaporto' },
  { value: 'patente_guida', label: 'Patente di guida' },
  { value: 'permesso_soggiorno', label: 'Permesso di soggiorno' }
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_DIMENSION = 2000;
const JPEG_QUALITY = 0.75;

const DocumentSection = ({ documentType, documentFile, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'error' | null
  const [statusMessage, setStatusMessage] = useState('');

  // Client-side image optimization
  const optimizeImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const optimizedFile = new File(
                  [blob],
                  file.name.replace(/\.[^.]+$/, '.jpg'),
                  { type: 'image/jpeg' }
                );
                resolve(optimizedFile);
              } else {
                reject(new Error('Failed to optimize image'));
              }
            },
            'image/jpeg',
            JPEG_QUALITY
          );
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Upload file to Supabase Storage
  const uploadToStorage = async (file) => {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Generate file path
      const timestamp = Date.now();
      const fileExtension = file?.name?.split('.')?.pop();
      const fileName = `${timestamp}.${fileExtension}`;
      const filePath = `user_${user?.id}/identity/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase?.storage?.from('identity-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      return {
        path: data?.path,
        size: file?.size,
        type: file?.type
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const files = e?.target?.files;
    
    if (!files || files?.length === 0) return;

    // Check if multiple files are selected
    if (files?.length > 1) {
      setUploadStatus('error');
      setStatusMessage('Puoi caricare un solo documento.');
      e.target.value = ''; // Reset input
      return;
    }

    const file = files?.[0];

    // Check if a file is already uploaded
    if (documentFile && documentFile?.storagePath) {
      // File already exists - this is a replacement action
      // Allow it to proceed (user clicked to change file)
    }

    // Reset status
    setUploadStatus(null);
    setStatusMessage('');
    setUploadProgress(0);

    // Validate file size
    if (file?.size > MAX_FILE_SIZE) {
      setUploadStatus('error');
      setStatusMessage('Il file supera il limite di 10MB');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes?.includes(file?.type)) {
      setUploadStatus('error');
      setStatusMessage('Formato file non valido. Usa JPG, PNG o PDF');
      e.target.value = ''; // Reset input
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(20);

      let fileToUpload = file;

      // Optimize if image
      if (file?.type?.startsWith('image/')) {
        setUploadProgress(40);
        fileToUpload = await optimizeImage(file);
      }

      setUploadProgress(60);

      // Upload to Supabase Storage
      const uploadResult = await uploadToStorage(fileToUpload);

      setUploadProgress(100);

      // Save metadata via existing data flow
      onChange?.('documentFile', {
        name: file?.name,
        originalSize: file?.size,
        optimizedSize: fileToUpload?.size,
        storagePath: uploadResult?.path,
        type: file?.type
      });

      setUploadStatus('success');
      setStatusMessage('Documento caricato con successo');
    } catch (error) {
      console.error('File upload error:', error);
      setUploadStatus('error');
      setStatusMessage(error?.message || 'Errore durante il caricamento del documento');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input after upload
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Documento di identità
      </h2>
      
      <p className="text-sm text-gray-600 mb-4">
        Carica un documento di identità valido per completare la verifica del profilo. 
        Questo documento è utilizzato esclusivamente per la verifica della sicurezza e non sarà condiviso pubblicamente.
      </p>
      
      <div className="space-y-4">
        <Select
          label="Tipo di documento"
          placeholder="Seleziona il tipo di documento"
          options={DOCUMENT_TYPES}
          value={documentType}
          onChange={(value) => onChange?.('documentType', value)}
          disabled={uploading}
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Carica documento
          </label>
          
          <div className="relative">
            <input
              type="file"
              id="document-upload"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="sr-only"
              disabled={uploading}
            />
            
            <label
              htmlFor="document-upload"
              className={cn(
                "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg transition-colors",
                uploading ? "border-gray-300 bg-gray-100 cursor-not-allowed" :
                documentFile 
                  ? "border-blue-500 bg-blue-50 cursor-pointer hover:bg-blue-100" :"border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer"
              )}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {uploading ? (
                  <>
                    <Loader2 className="h-8 w-8 mb-2 text-blue-600 animate-spin" />
                    <p className="text-sm font-medium text-blue-600">
                      Caricamento in corso... {uploadProgress}%
                    </p>
                  </>
                ) : documentFile ? (
                  <>
                    <CheckCircle className="h-8 w-8 mb-2 text-blue-600" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-blue-600">
                        {documentFile?.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Clicca per cambiare file
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 mb-2 text-gray-400" />
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Clicca per caricare</span> o trascina qui
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PDF, JPG, PNG (max 10MB)
                      </p>
                    </div>
                  </>
                )}
              </div>
            </label>
          </div>

          {/* Upload progress bar */}
          {uploading && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Status messages */}
          {uploadStatus === 'success' && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{statusMessage}</span>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{statusMessage}</span>
            </div>
          )}

          {/* Helper text */}
          <p className="mt-2 text-xs text-gray-500">
            Il documento viene caricato in modo sicuro e utilizzato solo per la verifica della tua identità. 
            Le immagini vengono automaticamente ottimizzate prima del caricamento.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentSection;